/**
 * A team-board task's description snapshot, stored compact (sync size fix,
 * 14.10.2026) — the task twin of the documents' `bodyBlocks`
 * (documents/docBodyCodec.js).
 *
 * In memory (and for every caller) a task carries the editor snapshot as
 * `descriptionLexical` ({ version: 1, blocks }). In the stored record it is
 * `descriptionBlocks` (compact) and `descriptionLexical` is not written.
 * boardsStore is the only place that converts: `decomposeBoard` ->
 * `toStoredTask`, `assembleBoard` / `patchBoardWithRecords` -> `fromStoredTask`.
 *
 * Older apps. They know only `descriptionLexical`, and unlike a document body a
 * task record is saved by spreading the whole in-memory task — so an older app
 * CARRIES our `descriptionBlocks` forward untouched while it changes the
 * description. Two rules keep that from ever showing a stale description:
 *
 *  1. a stored `descriptionLexical` wins over `descriptionBlocks` (only an
 *     older app writes it, and only together with the description it edited);
 *  2. `descriptionBlocks.hh` is a hash of the `descriptionHtml` it was saved
 *     with: when the HTML no longer matches (an older app's MCP replaced the
 *     description and did not know to clear our snapshot), the snapshot is
 *     dropped and the task opens from its HTML, as any task without one does.
 *
 * The converted key takes the SAME position in the object as the one it
 * replaces, so a decompose -> patch round trip (boardDiskRebase) leaves a task
 * JSON-identical and `sameJson` does not see a change that is not there.
 */
import { compactSnapshot, expandSnapshot } from '../documents/docBodyCodec';

export const STORED_KEY = 'descriptionBlocks';
export const MEMORY_KEY = 'descriptionLexical';

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** FNV-1a (32-bit, base 36) of the description HTML — a staleness check, not security. */
export function htmlHash(html) {
  let h = 0x811c9dc5;
  const s = String(html || '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** `obj` with `from` replaced IN PLACE by `to` = `value` (omitted when undefined). */
function replaceKey(obj, from, to, value) {
  const out = {};
  for (const [key, v] of Object.entries(obj)) {
    if (key === from) {
      if (value !== undefined) out[to] = value;
    } else if (key !== to) {
      out[key] = v;
    }
  }
  return out;
}

/** A task as the app holds it -> the `data` fields to store. */
export function toStoredTask(task) {
  if (!task || !has(task, MEMORY_KEY)) return task;
  const snapshot = task[MEMORY_KEY];
  if (snapshot == null) return replaceKey(task, MEMORY_KEY, STORED_KEY, undefined);
  const compact = compactSnapshot(snapshot);
  // Not a block snapshot: kept verbatim under its old name, never dropped.
  if (!compact) return task;
  compact.hh = htmlHash(task.descriptionHtml);
  return replaceKey(task, MEMORY_KEY, STORED_KEY, compact);
}

/** A stored task row (raw or flattened record) -> the task as the app holds it. */
export function fromStoredTask(task) {
  if (!task || !has(task, STORED_KEY)) return task;
  const stored = task[STORED_KEY];
  // Rule 1: an older app's own snapshot is the newer one. (No snapshot stored:
  // nothing to expand either.)
  if (task[MEMORY_KEY] != null || !stored) {
    const { [STORED_KEY]: _dropped, ...rest } = task;
    return rest;
  }
  const snapshot = expandSnapshot(stored);
  // Not a compact snapshot (a newer format?): left exactly as stored.
  if (!snapshot) return task;
  // Rule 2: the description was replaced without us — open it from its HTML.
  if (stored.hh != null && stored.hh !== htmlHash(task.descriptionHtml)) {
    return replaceKey(task, STORED_KEY, MEMORY_KEY, undefined);
  }
  return replaceKey(task, STORED_KEY, MEMORY_KEY, snapshot);
}
